/**
 * Date and time functions of the Core_Formula_Set (Requirement 4.1).
 *
 * Dates are serial numbers (see `dates.ts`), so `YEAR`, `MONTH`, `DAY`, `HOUR`,
 * `MINUTE`, and `WEEKDAY` are pure arithmetic on their numeric argument. A
 * negative serial has no calendar meaning and yields `#NUM!`, as in Excel.
 *
 * `TODAY` and `NOW` read the clock through `EvalContext.now()` and are declared
 * volatile in the registry, which is what makes them refresh once per
 * Recalculation_Cycle instead of once per read.
 */

import { err, num, type EvalContext, type FnSpec, type Value } from '../values.js';
import {
  endOfMonthSerial,
  partsFromSerial,
  serialFromMs,
  serialFromParts,
  wholeMonthsBetween,
} from '../dates.js';
import {
  failure,
  intArg,
  isFailure,
  numberArg,
  optIntArg,
  textArg,
  type Failure,
} from './helpers.js';

/** Reads a serial-date argument, rejecting negative serials with `#NUM!`. */
function serialArg(args: readonly Value[], i: number, ctx: EvalContext): number | Failure {
  const n = numberArg(args, i, ctx);
  if (isFailure(n)) return n;
  if (n < 0) return failure('#NUM!');
  return n;
}

/** One-argument extractor over a serial date. */
function datePart(name: string, pick: (serial: number) => number): FnSpec {
  return {
    name,
    min: 1,
    max: 1,
    fn: (args, ctx) => {
      const serial = serialArg(args, 0, ctx);
      if (isFailure(serial)) return serial.fail;
      return num(pick(serial));
    },
  };
}

export const DATETIME_FUNCTIONS: readonly FnSpec[] = [
  {
    name: 'TODAY',
    min: 0,
    max: 0,
    volatile: true,
    fn: (_args, ctx) => num(Math.floor(serialFromMs(ctx.now()))),
  },
  {
    name: 'NOW',
    min: 0,
    max: 0,
    volatile: true,
    // Truncated to whole seconds so equal instants compare equal.
    fn: (_args, ctx) => num(serialFromMs(Math.floor(ctx.now() / 1000) * 1000)),
  },
  {
    name: 'DATE',
    min: 3,
    max: 3,
    fn: (args, ctx) => {
      const year = intArg(args, 0, ctx);
      if (isFailure(year)) return year.fail;
      const month = intArg(args, 1, ctx);
      if (isFailure(month)) return month.fail;
      const day = intArg(args, 2, ctx);
      if (isFailure(day)) return day.fail;
      const serial = serialFromParts(year, month, day);
      if (!Number.isFinite(serial) || serial < 0) return err('#NUM!');
      return num(serial);
    },
  },
  datePart('YEAR', (s) => partsFromSerial(s).year),
  datePart('MONTH', (s) => partsFromSerial(s).month),
  datePart('DAY', (s) => partsFromSerial(s).day),
  datePart('HOUR', (s) => partsFromSerial(s).hour),
  datePart('MINUTE', (s) => partsFromSerial(s).minute),
  {
    name: 'WEEKDAY',
    min: 1,
    max: 2,
    fn: (args, ctx) => {
      const serial = serialArg(args, 0, ctx);
      if (isFailure(serial)) return serial.fail;
      const type = optIntArg(args, 1, ctx, 1);
      if (isFailure(type)) return type.fail;
      const sunday0 = partsFromSerial(serial).weekday;
      switch (type) {
        case 1:
          return num(sunday0 + 1);
        case 2:
          return num(((sunday0 + 6) % 7) + 1);
        case 3:
          return num((sunday0 + 6) % 7);
        default:
          return err('#NUM!');
      }
    },
  },
  {
    name: 'EOMONTH',
    min: 2,
    max: 2,
    fn: (args, ctx) => {
      const serial = serialArg(args, 0, ctx);
      if (isFailure(serial)) return serial.fail;
      const months = intArg(args, 1, ctx);
      if (isFailure(months)) return months.fail;
      const result = endOfMonthSerial(serial, months);
      if (!Number.isFinite(result) || result < 0) return err('#NUM!');
      return num(Math.floor(result));
    },
  },
  {
    name: 'DATEDIF',
    min: 3,
    max: 3,
    fn: (args, ctx) => {
      const start = serialArg(args, 0, ctx);
      if (isFailure(start)) return start.fail;
      const end = serialArg(args, 1, ctx);
      if (isFailure(end)) return end.fail;
      const unit = textArg(args, 2, ctx);
      if (isFailure(unit)) return unit.fail;
      if (end < start) return err('#NUM!');
      const from = partsFromSerial(start);
      const to = partsFromSerial(end);
      switch (unit.trim().toUpperCase()) {
        case 'D':
          return num(Math.floor(end) - Math.floor(start));
        case 'M':
          return num(wholeMonthsBetween(from, to));
        case 'Y': {
          const months = wholeMonthsBetween(from, to);
          return num(Math.floor(months / 12));
        }
        case 'MD': {
          // Days ignoring months and years; a negative difference borrows the
          // length of the month before the end date.
          if (to.day >= from.day) return num(to.day - from.day);
          const previousMonthEnd = serialFromParts(to.year, to.month, 0);
          const daysInPreviousMonth = partsFromSerial(previousMonthEnd).day;
          return num(daysInPreviousMonth - from.day + to.day);
        }
        case 'YM':
          return num(wholeMonthsBetween(from, to) % 12);
        case 'YD': {
          const years = Math.floor(wholeMonthsBetween(from, to) / 12);
          const anchor = serialFromParts(from.year + years, from.month, from.day);
          return num(Math.floor(end) - Math.floor(anchor));
        }
        default:
          return err('#NUM!');
      }
    },
  },
];
